import { PlayerOptionsInterface, ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import * as avia from '@cbsinteractive/avia-js/dist/types';
import { VideoPlayerInterface } from '@cbsinteractive/avia-js/dist/types/';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { IPlayerFactory } from 'library/ts/player/adapter/IPlayerFactory';
import { TagValidationUtils } from 'tag/ts/smart_tag/operations/types/TagValidationUtils';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { AviaPlayerWrapper } from 'tag/ts/tag/player/adapters/avia/AviaPlayerWrapper';
import { IResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/interfaces/IResourceConfigBuilder';
import { DesktopAssetUrlResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/DesktopAssetUrlResourceConfigBuilder';
import { DesktopCIDResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/DesktopCIDResourceConfigBuilder';
import { DesktopPIDResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/DesktopPIDResourceConfigBuilder';
import { HTML5AssetUrlResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/HTML5AssetUrlResourceConfigBuilder';
import { HTML5CIDResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/HTML5CIDResourceConfigBuilder';
import { HTML5PIDResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/HTML5PIDResourceConfigBuilder';

// declare VTG namespaces
declare global {
	interface Window {
		// Composite AVIA namespace (includes avia ui js)
		avia: typeof avia & { ui?: { createUi?: any } };
		// VTG namespace
		vtg: vtg;
		// DIT DataHub namespace
		dit: Dit;
		// utag namespace
		utag: any;
		// WebOS / LG SDK script namespaces https://avia.vtg.paramount.tech/avia-js/latest/docs/guides/lg#webos-device-info-api
		webOS: any;
		webOSSystem: any;
		PalmSystem: any;
		// Samsung Tizen API https://avia.vtg.paramount.tech/avia-js/latest/docs/guides/samsung#samsung-tizen-apis
		webapis: any;
	}
}

export interface vtg {
	ts: {
		Event: any;
		AviaTracking: any; // Avia tracking service namespace
	},
	config: any;
}

export interface Dit {
	svc: {
		avia: Record<string, unknown>; // DIT DataHub avia namespace (.plugin is accessed by AviaPlugin base class)
	}
}

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description VTG Avia player factory implementing Factory pattern for player lifecycle
 *
 * **Factory Pattern Implementation:**
 * Encapsulates VTG Avia player creation and destruction logic, providing a clean
 * interface for Smart Tag to manage VTG player instances without direct VTG API coupling.
 * Handles platform-specific configuration and resource setup.
 *
 * **Resource Configuration Strategy:**
 * Uses Builder pattern via IResourceConfigBuilder to create platform and content-specific
 * VTG player configurations:
 * - **Platform Selection**: Desktop/Mobile Web vs HTML5 platform builders
 * - **Content Type**: PID, CID, or Asset URL content resolution builders
 * - **Configuration Assembly**: Combines platform + content type for optimal setup
 *
 * **Player Lifecycle Management:**
 * - **Creation**: Instantiates VTG player with appropriate configuration
 * - **Configuration**: Applies Smart Tag business logic to VTG player setup
 * - **Resource Injection**: Loads content configuration into VTG player
 * - **Destruction**: Clean teardown of VTG player instances and resources
 *
 * **VTG Integration Points:**
 * - **window.avia.createVideoPlayer()**: VTG player instantiation
 * - **ResourceConfiguration**: VTG content and platform configuration
 * - **window.avia.removeVideoPlayer()**: VTG player cleanup
 *
 * **Business Impact:**
 * Abstracts VTG player complexity from Smart Tag, enabling content-specific
 * optimization and platform adaptation while maintaining upgrade compatibility.
 */
export class AviaFactory extends AbstractTagClass implements IPlayerFactory {

	// for creating ResourceConfiguration instance
	#resourceConfigBuilder: IResourceConfigBuilder;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaFactory'
			}
		);
	}

	/**
	 * @description Initializes resourceConfigBuilder
	 */
	#initAviaResourceConfigBuilder(): Promise<IPromiseResolveGeneric & { resourceConfigBuilder: IResourceConfigBuilder }> {
		return new Promise(
			( resolve, reject ) => {
				const LOG_PREFIX = 'initAviaResourceConfigBuilder';
				this.#resourceConfigBuilder = undefined;
				const assetEmbedType = this.tag.model.contentUtilities.assetEmbedType( this.params );

				// Guard: validate the resolved embed type before allowing any builder to be
				// instantiated. CID is not a valid playback identifier for any embed type —
				// only PID and assetURL are supported. This is the last line of defence on the
				// AviaFactory layer — normalizeParams() and TagModel.checkParams() should have
				// already blocked CID upstream, but if either is bypassed (e.g. raw object
				// spreads into tag.params after normalisation) this will catch it and reject
				// cleanly rather than silently producing a broken resource configuration.
				const ASSET_EMBED_TYPE_LABEL = 'assetEmbedType:';
				const ASSET_EMBED_TYPE_NOT_SUPPORTED = 'assetEmbedType not supported... setting resourceConfigBuilder to undefined';
				const embedTypeValidation = TagValidationUtils.isValidEmbedType( assetEmbedType );
				if ( !embedTypeValidation.isValid ) {
					this?.logger?.error( LOG_PREFIX, embedTypeValidation.error, ASSET_EMBED_TYPE_LABEL, assetEmbedType );
					reject( { msg: embedTypeValidation.error, success: false } );
					return;
				}

				const initDesktopResourceConfigBuilder = () => {
					switch ( assetEmbedType ) {
						case AssetEmbedType.ASSET_URL:
							this.#resourceConfigBuilder = new DesktopAssetUrlResourceConfigBuilder( { tag: this.tag } );
							break;
						case AssetEmbedType.PID:
							this.#resourceConfigBuilder = new DesktopPIDResourceConfigBuilder( { tag: this.tag } );
							break;
						case AssetEmbedType.CID:
							this.#resourceConfigBuilder = new DesktopCIDResourceConfigBuilder( { tag: this.tag } );
							break;
						default:
							this?.logger?.warn( 'initDesktopResourceConfigBuilder', ASSET_EMBED_TYPE_NOT_SUPPORTED, ASSET_EMBED_TYPE_LABEL, assetEmbedType );
							this.#resourceConfigBuilder = undefined;
					}
				};
				const initHTML5ResourceConfigBuilder = () => {
					switch ( assetEmbedType ) {
						case AssetEmbedType.ASSET_URL:
							this.#resourceConfigBuilder = new HTML5AssetUrlResourceConfigBuilder( { tag: this.tag } );
							break;
						case AssetEmbedType.PID:
							this.#resourceConfigBuilder = new HTML5PIDResourceConfigBuilder( { tag: this.tag } );
							break;
						case AssetEmbedType.CID:
							this.#resourceConfigBuilder = new HTML5CIDResourceConfigBuilder( { tag: this.tag } );
							break;
						default:
							this?.logger?.warn( 'initHTML5ResourceConfigBuilder', ASSET_EMBED_TYPE_NOT_SUPPORTED, ASSET_EMBED_TYPE_LABEL, assetEmbedType );
							this.#resourceConfigBuilder = undefined;
					}
				};

				switch ( this.params.SYSTEM.embedType ) {
					case EmbedType.DESKTOP:
					case EmbedType.MOBILE_WEB:
						initDesktopResourceConfigBuilder();
						break;
					case EmbedType.HTML5:
						initHTML5ResourceConfigBuilder();
						break;
					default:
						this?.logger?.warn( LOG_PREFIX, 'embedType not supported... setting resourceConfigBuilder to undefined', 'params.SYSTEM.embedType:', this.params.SYSTEM.embedType );
						this.#resourceConfigBuilder = undefined;
				}
				this?.logger?.log( LOG_PREFIX, 'resourceConfigBuilder:', this.#resourceConfigBuilder );

				resolve( { msg: 'done', success: true, resourceConfigBuilder: this.#resourceConfigBuilder } );
			}
		);
	}

	/**
	 * @description Create and return avia video player instance
	 * @param {PlayerOptionsInterface} playerOptions
	 * @returns {Promise<IPromiseResolveGeneric & {player: VideoPlayerInterface}>}
	 */
	createPlayer( playerOptions: PlayerOptionsInterface ): Promise<IPromiseResolveGeneric & { player: VideoPlayerInterface }> {
		return new Promise(
			( resolve, reject ) => {
				// Add safety check for window.avia
				if ( !window.avia || !window.avia.createVideoPlayer ) {
					const error = 'Avia-js not loaded or createVideoPlayer method unavailable';
					this?.logger?.warn( 'createPlayer Error:', error );
					reject( { msg: 'Player creation failed', result: error } );
					return;
				}

				window.avia.createVideoPlayer( playerOptions ).then(
					( player ) => {
						this?.logger?.log( 'createPlayer Success:', 'AviaPlayer:', player );
						resolve( { msg: 'Player created', player: player } );
					}
				).catch(
					( error ) => {
						this?.logger?.warn( 'createPlayer Error:', error );
						reject( { msg: 'Player creation failed', result: error } );
					}
				);
			}
		);
	}

	/**
	 * @description Destroy player
	 * @param {AviaPlayerWrapper} aviaPlayerWrapper
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	destroyPlayer( aviaPlayerWrapper: AviaPlayerWrapper ): Promise<IPromiseResolveGeneric> {
		return new Promise(
			( resolve, reject ) => {
				// if player is undefined => resolve our promise right away
				if ( this.utils.Type.isEmpty( aviaPlayerWrapper?.API ) ) {
					this?.logger?.log( 'destroyPlayer: player instance is:', aviaPlayerWrapper, 'Resolve the promise' );
					resolve( { msg: 'Player destroyed', success: true } );
				} else {
					aviaPlayerWrapper.API.removePlayerEventListener().then(
						( data ) => {
							this?.logger?.log( 'destroyPlayer: unsubscribed from avia events' );
							this.#removeVideoPlayer( aviaPlayerWrapper, resolve, reject );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'destroyPlayer: removePlayerEventListener failed:', error );
							// Continue with player removal even if event cleanup failed
							this.#removeVideoPlayer( aviaPlayerWrapper, resolve, reject );
						}
					);
				}
			}
		);
	}

	/**
	 * @description Helper method to safely remove video player
	 * @param {AviaPlayerWrapper} aviaPlayerWrapper
	 * @param {(value: IPromiseResolveGeneric) => void} resolve
	 * @param {(reason?: any) => void} reject
	 */
	#removeVideoPlayer( aviaPlayerWrapper: AviaPlayerWrapper, resolve: ( value: IPromiseResolveGeneric ) => void, reject: ( reason?: any ) => void ): void {
		// Add safety check for window.avia
		if ( !window.avia || !window.avia.removeVideoPlayer ) {
			this?.logger?.warn( 'destroyPlayer: avia.removeVideoPlayer unavailable, cleaning up locally' );
			aviaPlayerWrapper.adaptedPlayerInstance = undefined;
			resolve( { msg: 'Player destroyed (local cleanup)', success: true } );
			return;
		}

		// otherwise, remove player
		window.avia.removeVideoPlayer( aviaPlayerWrapper?.adaptedPlayerInstance?.id ).then(
			() => {
				this?.logger?.log( 'destroyPlayer Success' );
				aviaPlayerWrapper.adaptedPlayerInstance = undefined; // kill avia instance
				resolve( { msg: 'Player destroyed', success: true } );
			}
		).catch(
			( error ) => {
				this?.logger?.warn( 'destroyPlayer Error:', error );
				// Always clear player reference even on destruction failure to prevent memory leaks
				aviaPlayerWrapper.adaptedPlayerInstance = undefined;
				reject( { msg: 'Player destruction failed', result: error } );
			}
		);
	}

	/**
	 * @description Creates and returns avia resource config
	 * @returns {Promise<IPromiseResolveGeneric & {resource: ResourceConfigurationInterface}>}
	 */
	getResourceConfig(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve, reject ) => {
				let baseResourceConfig: ResourceConfigurationInterface;

				// create instance of resource config builder
				this.#initAviaResourceConfigBuilder().then(
					( data ) => {
						if ( this.utils.Type.isFunction( data?.resourceConfigBuilder?.getResult ) ) {
							// get base resource config object
							return data.resourceConfigBuilder.getResult();
						} else {
							// resource config builder is undefined - this is an error condition
							throw new Error( 'Resource config builder is undefined or getResult method is not available' );
						}
					}
				).then(
					( data ) => {
						this?.logger?.log( 'this.#resourceConfigBuilder.getResult()=>', data );
						// Resource is already in avia-js format from the builders
						baseResourceConfig = data?.resource || {};

						// Return it as a Promise
						const promiseArray: { ( ...args ): Promise<any> }[] = [
							() => this.tag.model.siteUtilities.getSiteSessionData( `${this.name}.getResourceConfig` )
						];

						return this.utils.Function.runPromiseArray( promiseArray );
					}
				).then(
					() => {
						this?.logger?.log( 'getResourceConfig:', baseResourceConfig );
						resolve( { msg: 'Resource config', resource: baseResourceConfig as ResourceConfigurationInterface } );
					}
				).catch(
					( error ) => {
						this?.logger?.warn( 'Resource config creation failed:', error );
						reject( { msg: 'Resource config failed', result: error } );
					}
				);
			}
		);
	}

}